import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { apiClient } from "@/api";
import { User, OAuthConfig, TokenResponse } from "@/api/types";
import { success, error as showError, info } from "@/utils/toast";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  loginWithGoogle: (credentialResponse: any) => Promise<void>;
  loginWithPassword: (usernameOrEmail: string, password: string) => Promise<void>;
  doTokenRefresh: (refreshToken: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
  oauthConfig: OAuthConfig | null | undefined;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [oauthConfig, setOauthConfig] = useState<OAuthConfig | null | undefined>(undefined);

  // Load OAuth config on mount
  useEffect(() => {
    apiClient
      .getOAuthConfig()
      .then((config) => setOauthConfig(config))
      .catch((err) => {
        console.error("Failed to load OAuth config:", err);
        showError("Failed to load authentication configuration");
        // return some kind of error to make the caller reload
        setOauthConfig(null);
      });
  }, []);

  // Check if user is already authenticated on mount
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      apiClient
        .getCurrentUser()
        .then((userData) => {
          setUser(userData);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to fetch current user:", err);
          // showError("Session expired. Please log in again.");
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const userData = await apiClient.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error("Failed to fetch current user:", error);
      showError("Failed to fetch user information");
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async (credentialResponse: any): Promise<void> => {
    if (!credentialResponse.access_token) {
      throw new Error("No credential received from Google");
    }

    console.log("Google credential:", credentialResponse);

    try {
      // Convert Google credential to Django API token
      const apiTokens = await apiClient.convertGoogleToken(
        oauthConfig,
        credentialResponse.access_token,
      );

      // Store tokens
      localStorage.setItem("access_token", apiTokens.access_token);
      localStorage.setItem("refresh_token", apiTokens.refresh_token);

      // Fetch user data
      await fetchCurrentUser();

      success("Successfully logged in!");
    } catch (error) {
      showError("Login failed. Please try again.");
      throw error instanceof Error ? error : new Error("Authentication failed");
    }
  };

  const loginWithPassword = async (usernameOrEmail: string, password: string): Promise<void> => {
    if (!usernameOrEmail || !password) {
      throw new Error("Please provide both username/email and password");
    }

    try {
      const apiTokens = await apiClient.loginWithPassword(
        usernameOrEmail,
        password,
        oauthConfig ?? null,
      );

      localStorage.setItem("access_token", apiTokens.access_token);
      localStorage.setItem("refresh_token", apiTokens.refresh_token);

      await fetchCurrentUser();

      success("Successfully logged in!");
    } catch (err) {
      showError("Login failed. Please check your credentials.");
      throw err instanceof Error ? err : new Error("Authentication failed");
    }
  };

  const doTokenRefresh = async (refreshToken: string) => {
    try {
      const newTokens = await apiClient.refreshToken(refreshToken, oauthConfig);
      localStorage.setItem("access_token", newTokens.access_token);
      localStorage.setItem("refresh_token", newTokens.refresh_token);
      success("Tokens refreshed successfully!");
    } catch (error) {
      console.error("Failed to refresh token:", error);
      showError("Failed to refresh session. Please log in again.");
      logout();
    }
  }

  const logout = () => {
    // log them out on the backend, first, to clear session cookies
    apiClient.post("/api-auth/logout/").catch((err) => {
      console.error("Failed to log out on server:", err);
    });

    setUser(null);
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");

    info("You have been logged out successfully");
  };

  const value: AuthContextType = {
    user,
    loading,
    loginWithGoogle,
    loginWithPassword,
    doTokenRefresh,
    logout,
    refreshUser: fetchCurrentUser,
    isAuthenticated: !!user,
    oauthConfig,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
